import React from "react";
import {
  Box,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Typography,
  ListItemText,
  styled,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// ✅ Custom styled checkbox
const CustomCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "checkedState",
})(({ checkedState }) => ({
  color: checkedState ? "var(--brand-600)" : "var(--gray-500)",
  borderRadius: "10px",
  padding: "4px",
  "&.Mui-checked": {
    color: "var(--brand-800)",
  },
  "& .MuiSvgIcon-root": {
    borderRadius: "10px",
    fontSize: "20px",
  },
  "&:hover": {
    backgroundColor: "var(--brand-50)",
  },
}));

export default function CheckboxBox({
  label,
  type = "single",          // "single" or "multi"
  checkboxLabel = "",
  options = [],
  value = type === "multi" ? [] : false,  // boolean for single, array for multi
  onChange,
  sx = {},
  placeholder = "-",
  disabled = false,
  width,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [check, setCheck] = React.useState(type === "multi" ? [] : Boolean(value));
  // ✅ Sync state when value changes from outside
  React.useEffect(() => {
    if (type === "multi") {
      setCheck(Array.isArray(value) ? value : []);
    } else {
      setCheck(Boolean(value));
    }
  }, [value, type]);

  // ✅ Single checkbox toggle
  const handleSingleToggle = () => {
    const newVal = !check;
    setCheck(newVal);
    onChange && onChange(newVal);
  };

  // ✅ Multi-select open/close
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // ✅ Multi checkbox toggle
  const handleMultiToggle = (option) => {
    const newValue = check.includes(option)
      ? check.filter((v) => v !== option)
      : [...check, option];
    setCheck(newValue);
    onChange && onChange(newValue);
  };

  const displayText =
    type === "multi" && check.length > 0 ? check.join(", ") : placeholder;

  const outerBoxStyles = { display: "flex", alignItems: "center", gap: 1, ...sx };
  const paperStyles = {
    display: "flex",
    alignItems: "center",
    borderRadius: 2,
    border: "1px solid var(--gray-600)",
    height: sx?.height || 40,
    width: width || sx?.width,   // ← Add this line
    overflow: "hidden",
    bgcolor: "var(--white)",
  };

  return (
    <Box sx={outerBoxStyles}>
      {label && <Typography variant="bodySmall">{label}</Typography>}

      {type === "single" ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={check}
            onChange={handleSingleToggle}
            checkedState={check}
            size="small"
            disabled={disabled}
            sx={{ borderRadius: 3 }}
          />
          <Typography sx={{ fontSize: 14 }}>{checkboxLabel}</Typography>
        </Box>
      ) : (
        <Paper variant="outlined" sx={paperStyles}>
          <Box
            sx={{
              flex: 1,
              px: 2,
              display: "flex",
              alignItems: "center",
              color: check.length > 0 ? "inherit" : "var(--gray-300)",
              height: "100%",
              borderRadius: 2,
            }}
          >
            {displayText}
          </Box>
          <IconButton size="small" onClick={handleOpen}>
            <CheckBoxIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            {options.map((option, idx) => (
              <MenuItem key={idx} disableGutters>
                <CustomCheckbox
                  disabled={disabled}
                  checked={check.includes(option)}
                  onChange={() => handleMultiToggle(option)}
                  checkedState={check.includes(option)}
                  size="small"
                  sx={{ ml: 1 }}
                />
                <ListItemText primary={option} sx={{ ml: 1 }} />
              </MenuItem>
            ))}
          </Menu>
        </Paper>
      )}
    </Box>
  );
}

